import { E3Island, G1Nowifi, J1ToprightarrowC, J1DownleftarrowC, J2Lock } from '@e3dc-react/icons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Typography } from '@mui/material'
import React, { useCallback, useContext, useMemo } from 'react'
import { useSocketConnectionContext } from 'src/Elements/Framework/SystemDataProvider'
import { EmergencyPowerStatus } from 'src/Libs/emergency-power/types'
import { InstallationOnlineStatusContext } from 'src/Libs/installations/context/installation-online-status-context'
import { useTypedSelector } from 'src/State/RootReducer'

export type BatteryWidgetData = {
  stateOfCharge: number
  chargingValue: number
  isCharging: boolean
  isDischarging: boolean
  isChargeLockActive: boolean
  isDischargeLockActive: boolean
  emergencyPowerIsActive: boolean
  subtitle: React.ReactNode
  isBatteryWidgetDataLoading: boolean
}

const ICON_FONT_SIZE = 12

/*
 * A hook that fetches the data for the battery widget.
 */
const useDataForBatteryWidget = (): BatteryWidgetData => {
  const { t } = useCustomTranslation()
  const isStorageLoading = useTypedSelector(state => state.storage.storageLoading)
  const liveData = useTypedSelector(state => state.storage.liveData)
  const { isStorageOnline } = useContext(InstallationOnlineStatusContext)

  const stateOfCharge = liveData?.socCurrent ?? 0
  const chargingValue = liveData?.powerBat ?? 0
  const isCharging = (!!liveData?.powerBat && Math.sign(liveData?.powerBat) === 1) ?? false
  const isDischarging = !!liveData?.powerBat && Math.sign(liveData?.powerBat) === -1
  const emergencyBatteryPowerIsActive = liveData?.emergencyBatteryMode?.emergencyPowerStatus === EmergencyPowerStatus.active
  const emergencyStoragePowerIsActive = liveData?.emergencyStorageMode?.emergencyPowerStatus === EmergencyPowerStatus.active
  const emergencyPowerIsActive = emergencyBatteryPowerIsActive || emergencyStoragePowerIsActive

  const { emergencyPowerBatterySocketState } = useContext(useSocketConnectionContext())
  const { data: emergencyPowerBatterySocketData } = emergencyPowerBatterySocketState
  const isChargeLockActive = emergencyPowerBatterySocketData?.isChargeLockActive || false
  const isDischargeLockActive = emergencyPowerBatterySocketData?.isDischargeLockActive || false
  const isLockActive = isChargeLockActive || isDischargeLockActive

  const getBatteryCardLoadingText = useCallback(
    (isCharging: boolean, isDischarging: boolean, isOffline: boolean, isChargeLockActive: boolean, isDischargeLockActive: boolean): string | null => {
      if (isOffline) return t('dashboard.liveView.widgets.offline')
      if (isChargeLockActive) return t('dashboard.live-view.widgets.locked-charging')
      if (isDischargeLockActive) return t('dashboard.live-view.widgets.locked-discharging')
      if (isCharging) return t('dashboard.live-view.widgets.charging')
      if (isDischarging) return t('dashboard.live-view.widgets.discharging')
      return null
    },
    [t]
  )

  const getBatteryCardIcon = (isCharging: boolean, isDischarging: boolean, isOffline: boolean, isLockActive: boolean): React.ReactNode => {
    if (isOffline) return <G1Nowifi sx={{ fontSize: 12, color: 'error.main' }} />
    if (isDischarging) return <J1DownleftarrowC sx={{ fontSize: ICON_FONT_SIZE }} />
    if (isCharging) return <J1ToprightarrowC sx={{ fontSize: ICON_FONT_SIZE }} />
    if (isLockActive) return <J2Lock sx={{ fontSize: ICON_FONT_SIZE }} />
    return null
  }

  const subtitle = useMemo(() => {
    const text = getBatteryCardLoadingText(isCharging, isDischarging, !isStorageOnline, isChargeLockActive, isDischargeLockActive)
    const icon = getBatteryCardIcon(isCharging, isDischarging, !isStorageOnline, isLockActive)
    const emergencyPowerText = t('dashboard.liveView.widgets.islandMode')
    const emergencyPowerIcon = <E3Island sx={{ fontSize: ICON_FONT_SIZE }} />

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {emergencyPowerIsActive && (
          <>
            <Typography variant="caption">{emergencyPowerText}</Typography> {emergencyPowerIcon}
          </>
        )}
        <Typography variant="caption">{text}</Typography>
        {icon}
      </Box>
    )
  }, [
    getBatteryCardLoadingText,
    isCharging,
    isDischarging,
    isStorageOnline,
    isChargeLockActive,
    isDischargeLockActive,
    isLockActive,
    t,
    emergencyPowerIsActive,
  ])

  return {
    stateOfCharge,
    chargingValue,
    isCharging,
    isDischarging,
    emergencyPowerIsActive,
    subtitle,
    isBatteryWidgetDataLoading: isStorageLoading,
    isChargeLockActive,
    isDischargeLockActive,
  }
}

export default useDataForBatteryWidget
